/**
 * OwnerAgreementRequiredPage.tsx
 *
 * Where everyone except the account owners waits while a practice's first owner
 * accepts its owner agreement. Signing in succeeded; the app is simply not
 * reachable yet.
 *
 * The hold is sourced from the auth store, which the login pages hydrate and
 * Layout refreshes from /auth/me, so a hard refresh cannot bypass it.
 *
 * Shares the shell of DisclaimerPage deliberately — a held user has just come
 * through the same sign-in and should not feel dropped somewhere else.
 */
import { useAuthStore } from '@/store/authStore';
import { redirectToCorpLogout } from '@/lib/corpAuth';
import { getLegacyOrigin } from '@/lib/appStage';

const PROSPERA_LOGO_URL = `${getLegacyOrigin()}/uimages/img8/brandlogo.png`;

export function OwnerAgreementRequiredPage() {
  const logout = useAuthStore((s) => s.logout);

  function handleSignOut() {
    logout();
    // Back to the corporate sign-in with the account list, so an owner can sign
    // in here without first clearing someone else's session.
    redirectToCorpLogout();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#040816] px-4 py-10 text-white">
      <div className="flex w-full max-w-xl flex-col">
        <div className="mb-6 flex justify-center">
          <img
            src={PROSPERA_LOGO_URL}
            alt="Prospera"
            className="h-auto w-full max-w-[220px] object-contain"
          />
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#141a2b] p-8 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
          <h1 className="text-xl font-semibold text-white">
            An owner needs to sign in first
          </h1>

          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            Before anyone else at your practice can use Prospera, an account
            owner has to read and accept the owner agreement.
          </p>

          <p className="mt-3 text-sm leading-relaxed text-slate-300">
            Ask an owner to sign in — they will be shown the agreement straight
            away. Once they have accepted it, sign in again and you will be able
            to carry on as normal.
          </p>

          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-lg border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-white/10"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OwnerAgreementRequiredPage;
